import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { twMerge } from 'tailwind-merge';

import { UnstyledButton } from '@paid-ui/components/unstyled-button';
import { ProjectStatus } from '@paid-ui/enums/project';

const tabs = [
  {
    value: ProjectStatus.ACTIVE,
    label: 'Current',
  },
  {
    value: ProjectStatus.ARCHIVED,
    label: 'Archived',
  },
];

interface Props {
  hidden?: boolean;
  disabled?: boolean;
  className?: string;
}

export const ProjectFilters: React.FC<Props> = ({ hidden, disabled, className }) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const activeTab = searchParams.get('projectStatus') || ProjectStatus.ACTIVE;

  if (hidden) {
    return null;
  }

  const handleTabChange: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    const value = event.currentTarget.dataset.tab;
    if (!value || value === activeTab) return;

    const newSearchParams = new URLSearchParams(searchParams);

    // Switching status changes what the list contains, so the current page is meaningless.
    newSearchParams.delete('page');

    if (value === ProjectStatus.ARCHIVED) {
      newSearchParams.set('projectStatus', value);
      newSearchParams.sort();
    } else {
      newSearchParams.delete('projectStatus');
    }

    router.push(
      {
        pathname: '/',
        query: newSearchParams.toString(),
      },
      undefined,
      {
        shallow: true,
        scroll: false,
      },
    );
  };

  return (
    <div className={twMerge('grid items-center gap-x-2 lg:justify-center', className)}>
      <div className="grid w-full grid-cols-2 items-center gap-x-1 rounded-[50px] border border-grey-hover bg-grey-light p-1 lg:w-60">
        {tabs.map((tab) => (
          <UnstyledButton
            key={tab.value}
            data-tab={tab.value}
            disabled={disabled}
            onClick={handleTabChange}
            className={twMerge(
              'w-auto cursor-pointer justify-center rounded-[46px] p-2.5 text-sm font-medium transition-all duration-150 ease-linear hover:text-blue disabled:cursor-not-allowed',
              activeTab === tab.value ? 'bg-white text-blue shadow' : '',
            )}
          >
            {tab.label}
          </UnstyledButton>
        ))}
      </div>
    </div>
  );
};
